import { useState, useMemo } from 'react';
import { BarChart2, TrendingUp, Users, Zap, AlertCircle, RefreshCw } from 'lucide-react';
import { useLogs, BOSH_FILTRELER } from '../hooks/useLogs';
import { MUSTERILER } from '../constants/filters';

const ZAMAN_SECENEKLERI = [
  { value: 'bugun', label: 'Bugün' },
  { value: 168,     label: 'Son 7 gün' },
  { value: 720,     label: 'Son 30 gün' },
];

function MetricKart({ baslik, deger, alt, renk, ikon: Ikon }) {
  return (
    <div className='bg-white rounded-xl border border-gray-200 p-5'>
      <div className='flex items-center justify-between mb-2'>
        <span className='text-xs font-medium text-slate-500 uppercase tracking-wider'>
          {baslik}
        </span>
        <Ikon size={16} className={renk} />
      </div>
      <p className='text-2xl font-bold text-slate-800'>{deger}</p>
      {alt && <p className='text-xs text-slate-400 mt-1'>{alt}</p>}
    </div>
  );
}

export default function UsagePage() {
  const [musteri, setMusteri] = useState('');
  const [sure, setSure]       = useState('bugun');

  const filtreler = useMemo(() => ({
    ...BOSH_FILTRELER,
    musteri,
    sure,
  }), [musteri, sure]);

  const { allData, loading, error, lastFetch, refresh } = useLogs(filtreler);

  // Client-side hesaplamalar
  const stats = useMemo(() => {
    if (!allData || allData.length === 0) {
      return {
        toplamIstek: 0,
        gunlukOrt: 0,
        benzersizKullanici: 0,
        ortSureMs: 0,
        hataliIstek: 0,
        validasyonluIstek: 0,
        modulDagilim: [],
        endpointDagilim: [],
        gunlukTrend: [],
      };
    }

    const toplamIstek = allData.length;
    const gun = sure === 'bugun' ? 1 : sure === 'dun' ? 1 : Math.max(1, Math.ceil(sure / 24));
    const gunlukOrt = Math.round(toplamIstek / gun);

    // Benzersiz kullanıcı
    const kullanicilar = new Set();
    allData.forEach(g => {
      const kid = g.request?.kullaniciId;
      if (kid && kid !== '-') kullanicilar.add(kid);
    });

    // Ortalama süre (process loglarındaki en büyük süre)
    let toplamSure = 0;
    let sureSayisi = 0;
    allData.forEach(g => {
      const sureler = g.process
        .map(p => parseInt(p.sureMs))
        .filter(n => !isNaN(n));
      if (sureler.length > 0) {
        toplamSure += Math.max(...sureler);
        sureSayisi++;
      }
    });
    const ortSureMs = sureSayisi > 0 ? Math.round(toplamSure / sureSayisi) : 0;

    // Hata ve validasyon sayıları
    const hataliIstek = allData.filter(g => g.renk === 'red').length;
    const validasyonluIstek = allData.filter(g => g.renk === 'yellow').length;

    // Modül dağılımı
    const modulMap = new Map();
    allData.forEach(g => {
      const modul = g.request?.modul ?? '-';
      if (modul === '-') return;
      modulMap.set(modul, (modulMap.get(modul) ?? 0) + 1);
    });
    const modulDagilim = Array.from(modulMap.entries())
      .map(([modul, sayi]) => ({ modul, sayi }))
      .sort((a, b) => b.sayi - a.sayi);

    // Endpoint dağılımı (top 10)
    const epMap = new Map();
    allData.forEach(g => {
      const ep = g.request?.endpoint ?? '-';
      if (ep === '-') return;
      epMap.set(ep, (epMap.get(ep) ?? 0) + 1);
    });
    const endpointDagilim = Array.from(epMap.entries())
      .map(([endpoint, sayi]) => ({ endpoint, sayi }))
      .sort((a, b) => b.sayi - a.sayi)
      .slice(0, 10);

    // Günlük trend
    const gunMap = new Map();
    allData.forEach(g => {
      const zaman = g.request?.zaman;
      if (!zaman || zaman === '-') return;
      let tarih;
      try {
        if (zaman.includes('T')) {
          tarih = new Date(zaman).toLocaleDateString('tr-TR');
        } else {
          const num = parseFloat(zaman);
          const ms = num > 9999999999 ? num : num * 1000;
          tarih = new Date(ms).toLocaleDateString('tr-TR');
        }
      } catch { return; }
      gunMap.set(tarih, (gunMap.get(tarih) ?? 0) + 1);
    });
    const gunlukTrend = Array.from(gunMap.entries())
      .map(([gun, sayi]) => ({ gun, sayi }))
      .sort((a, b) => {
        const [da, ma, ya] = a.gun.split('.').map(Number);
        const [db, mb, yb] = b.gun.split('.').map(Number);
        return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
      });

    return {
      toplamIstek,
      gunlukOrt,
      benzersizKullanici: kullanicilar.size,
      ortSureMs,
      hataliIstek,
      validasyonluIstek,
      modulDagilim,
      endpointDagilim,
      gunlukTrend,
    };
  }, [allData, sure]);

  const maxGunluk = Math.max(...stats.gunlukTrend.map(g => g.sayi), 1);
  const maxModul  = Math.max(...stats.modulDagilim.map(m => m.sayi), 1);
  const maxEp     = Math.max(...stats.endpointDagilim.map(e => e.sayi), 1);

  const musteriAdi = MUSTERILER.find(m => m.value === musteri)?.label;

  return (
    <div>
      {/* Başlık + Filtreler */}
      <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
        <div>
          <h1 className='text-xl font-bold text-slate-800'>Kullanım Analizi</h1>
          {lastFetch && (
            <p className='text-xs text-slate-400 mt-0.5'>
              Son güncelleme: {lastFetch.toLocaleTimeString('tr-TR')}
            </p>
          )}
        </div>
        <div className='flex items-center gap-3'>
          <select value={musteri} onChange={e => setMusteri(e.target.value)}
            className='border border-gray-200 rounded-lg px-3 py-1.5 text-sm
                       focus:ring-2 focus:ring-blue-400 focus:outline-none'>
            <option value='' disabled>Müşteri Seçin</option>
            {MUSTERILER.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select value={sure} onChange={e => {
              const val = e.target.value;
              const parsed = Number(val);
              setSure(isNaN(parsed) ? val : parsed);
            }}
            className='border border-gray-200 rounded-lg px-3 py-1.5 text-sm
                       focus:ring-2 focus:ring-blue-400 focus:outline-none'>
            {ZAMAN_SECENEKLERI.map(z => (
              <option key={z.value} value={z.value}>{z.label}</option>
            ))}
          </select>
          <button onClick={refresh}
            className='p-2 border border-gray-200 rounded-lg hover:bg-gray-50
                       transition-colors'>
            <RefreshCw size={14}
              className={loading ? 'animate-spin text-blue-500' : 'text-gray-400'} />
          </button>
        </div>
      </div>

      {/* Müşteri seçili değilse */}
      {!musteri ? (
        <div className='flex flex-col items-center justify-center py-20
                        bg-white rounded-xl border border-gray-200'>
          <div className='w-12 h-12 bg-blue-50 rounded-full flex items-center
                          justify-center mb-3'>
            <span className='text-2xl'>👥</span>
          </div>
          <p className='text-slate-500 font-medium'>Müşteri seçimi yapın</p>
          <p className='text-slate-400 text-sm mt-1'>
            Kullanım analizini görüntülemek için yukarıdan bir müşteri seçin
          </p>
        </div>
      ) : error ? (
        <div className='bg-red-50 border border-red-200 rounded-xl p-4
                        text-sm text-red-600 flex items-center gap-2'>
          <AlertCircle size={16} />
          {error}
        </div>
      ) : loading ? (
        <div className='flex items-center justify-center py-20'>
          <div className='text-center'>
            <div className='w-8 h-8 border-2 border-blue-500 border-t-transparent
                            rounded-full animate-spin mx-auto mb-3' />
            <p className='text-sm text-slate-400'>Veriler hesaplanıyor...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Özet Kartlar */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
            <MetricKart
              baslik='Toplam İstek'
              deger={stats.toplamIstek.toLocaleString('tr-TR')}
              alt={musteriAdi}
              renk='text-blue-500'
              ikon={Zap}
            />
            <MetricKart
              baslik='Günlük Ortalama'
              deger={stats.gunlukOrt.toLocaleString('tr-TR')}
              alt='istek/gün'
              renk='text-green-500'
              ikon={TrendingUp}
            />
            <MetricKart
              baslik='Benzersiz Kullanıcı'
              deger={stats.benzersizKullanici.toLocaleString('tr-TR')}
              alt='aktif kullanıcı'
              renk='text-purple-500'
              ikon={Users}
            />
            <MetricKart
              baslik='Ort. Yanıt Süresi'
              deger={`${stats.ortSureMs.toLocaleString('tr-TR')} ms`}
              alt='tüm istekler'
              renk='text-amber-500'
              ikon={BarChart2}
            />
          </div>

          {/* Hata / Validasyon özeti */}
          <div className='grid grid-cols-2 gap-4 mb-6'>
            <div className='bg-red-50 border border-red-200 rounded-xl p-4
                            flex items-center justify-between'>
              <div>
                <p className='text-xs text-red-500 font-medium'>Hatalı İstekler</p>
                <p className='text-xl font-bold text-red-600'>{stats.hataliIstek}</p>
              </div>
              <span className='text-xs text-red-400'>
                %{stats.toplamIstek > 0
                  ? ((stats.hataliIstek / stats.toplamIstek) * 100).toFixed(1)
                  : '0'}
              </span>
            </div>
            <div className='bg-amber-50 border border-amber-200 rounded-xl p-4
                            flex items-center justify-between'>
              <div>
                <p className='text-xs text-amber-500 font-medium'>Validasyon Uyarısı</p>
                <p className='text-xl font-bold text-amber-600'>{stats.validasyonluIstek}</p>
              </div>
              <span className='text-xs text-amber-400'>
                %{stats.toplamIstek > 0
                  ? ((stats.validasyonluIstek / stats.toplamIstek) * 100).toFixed(1)
                  : '0'}
              </span>
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>

            {/* Günlük Trend */}
            <div className='bg-white rounded-xl border border-gray-200 p-5'>
              <h3 className='text-sm font-semibold text-slate-700 mb-4'>
                Günlük İstek Trendi
              </h3>
              {stats.gunlukTrend.length === 0 ? (
                <p className='text-xs text-slate-400'>Veri bulunamadı.</p>
              ) : (
                <div className='space-y-1.5 max-h-64 overflow-y-auto'>
                  {stats.gunlukTrend.map((g, i) => (
                    <div key={i} className='flex items-center gap-3'>
                      <span className='text-xs text-slate-400 w-20 shrink-0'>
                        {g.gun}
                      </span>
                      <div className='flex-1 bg-gray-100 rounded-full h-2'>
                        <div className='bg-blue-500 h-2 rounded-full transition-all'
                          style={{ width: `${(g.sayi / maxGunluk) * 100}%` }} />
                      </div>
                      <span className='text-xs font-medium text-slate-600 w-10 text-right'>
                        {g.sayi}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modül Dağılımı */}
            <div className='bg-white rounded-xl border border-gray-200 p-5'>
              <h3 className='text-sm font-semibold text-slate-700 mb-4'>
                Modül Bazlı Dağılım
              </h3>
              {stats.modulDagilim.length === 0 ? (
                <p className='text-xs text-slate-400'>Veri bulunamadı.</p>
              ) : (
                <div className='space-y-3'>
                  {stats.modulDagilim.map((m, i) => (
                    <div key={i}>
                      <div className='flex justify-between text-xs text-slate-600 mb-1'>
                        <span className='font-medium'>{m.modul}</span>
                        <span>{m.sayi} istek</span>
                      </div>
                      <div className='bg-gray-100 rounded-full h-2'>
                        <div className='bg-indigo-500 h-2 rounded-full'
                          style={{ width: `${(m.sayi / maxModul) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Endpoint Dağılımı (Top 10) */}
            <div className='lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5'>
              <h3 className='text-sm font-semibold text-slate-700 mb-4'>
                En Çok Kullanılan Endpoint'ler
                <span className='text-xs text-slate-400 font-normal ml-2'>(Top 10)</span>
              </h3>
              {stats.endpointDagilim.length === 0 ? (
                <p className='text-xs text-slate-400'>Veri bulunamadı.</p>
              ) : (
                <div className='space-y-2'>
                  {stats.endpointDagilim.map((e, i) => (
                    <div key={i} className='flex items-center gap-3'>
                      <span className='text-xs text-slate-400 w-5 text-right shrink-0'>
                        {i + 1}.
                      </span>
                      <code className='text-xs text-slate-600 font-mono w-64 shrink-0 truncate'
                            title={e.endpoint}>
                        {e.endpoint}
                      </code>
                      <div className='flex-1 bg-gray-100 rounded-full h-2'>
                        <div className='bg-teal-500 h-2 rounded-full'
                          style={{ width: `${(e.sayi / maxEp) * 100}%` }} />
                      </div>
                      <span className='text-xs font-medium text-slate-600 w-10 text-right'>
                        {e.sayi}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
